import * as React from "react"
import { useState } from "react"
import { Avatar, Box, Chip, CircularProgress, IconButton, Link, Tooltip, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit } from "@ui-seeker/utils"

import { editMappingDrawer, imageComparisonModal, removeProductFromGroup } from "../@signals"


function SeenDate({ label, value }) {
    return <Box sx={{ minWidth: 92 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value || '—'}
        </Typography>
    </Box>
}


export default function ProductRow({ group, product }) {
    const { sku } = group
    const { showToast } = useAppToast()
    const [isRemoving, setIsRemoving] = useState(false)

    const openComparison = () => {
        imageComparisonModal.value = {
            title: product.title,
            variantRaw: product.variant_raw,
            rawImage: product.image,
            skuImage: sku.image,
            skuName: sku.name
        }
    }

    const openEdit = () => {
        editMappingDrawer.value = {
            raw: {
                product_id: product.product_id,
                id_site: product.id_site,
                // The drawer lists the brand's SKUs: fall back to our SKU's brand when the raw has none
                id_brand: product.id_brand || sku.id_brand,
                image: product.image,
                title: product.title,
                variant_raw: product.variant_raw,
                site: group.site,
                brand: sku.brand
            },
            sku
        }
    }

    // Same request as the drawer's Remove, without opening it
    const removeMapping = () => showToast.confirm(
        `Remove the mapping of product ${product.product_id} to SKU ${sku.id_sku}?`,
        {
            onSubmit: async () => {
                setIsRemoving(true)
                try {
                    await postSubmit(`/api/mapping/sku`, {
                        productID: product.product_id,
                        siteID: product.id_site,
                        skuID: 0,
                        source: 'sku-duplicates'
                    })

                    removeProductFromGroup(product.id_site, sku.id_sku, product.product_id)
                    showToast.success('Mapping removed')
                } catch (error) {
                    showToast.error('Error removing mapping')
                } finally {
                    setIsRemoving(false)
                }
            }
        }
    )

    return <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1,
        px: 1,
        borderTop: '1px solid #f3f4f6',
        '&:hover': { backgroundColor: '#f9fafb' }
    }}>
        <Avatar
            src={product.image}
            variant="rounded"
            onClick={openComparison}
            sx={{
                width: 64,
                height: 64,
                bgcolor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                cursor: 'zoom-in',
                '& img': { objectFit: 'contain' }
            }}
        >
            <IconSeeker icon="image" sx={{ color: '#9ca3af' }} />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={product.title || ''}>
                {product.title || 'No raw data'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                {product.product_url ? (
                    <Link
                        href={product.product_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{ fontFamily: 'monospace' }}
                    >
                        {product.product_id}
                    </Link>
                ) : (
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {product.product_id}
                    </Typography>
                )}
                {product.variant_raw && (
                    <Chip
                        label={product.variant_raw}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#e0e7ff', color: '#4338ca' }}
                    />
                )}
                {!product.active && (
                    <Tooltip title="Not seen in the last 30 days">
                        <Chip label="Inactive" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', color: '#6b7280' }} />
                    </Tooltip>
                )}
                {product.validated && (
                    <Chip label="Validated" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
            </Box>
        </Box>

        <SeenDate label="First seen" value={product.first_seen} />
        <SeenDate label="Last seen" value={product.last_seen} />

        <Tooltip title="Edit mapping">
            <IconButton size="small" color="primary" onClick={openEdit}>
                <IconSeeker icon="pen-to-square" />
            </IconButton>
        </Tooltip>

        <Tooltip title="Remove mapping">
            <span>
                <IconButton size="small" color="error" onClick={removeMapping} disabled={isRemoving}>
                    {isRemoving ? <CircularProgress size={16} /> : <IconSeeker icon="link-slash" />}
                </IconButton>
            </span>
        </Tooltip>
    </Box>
}
